import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Upload, Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", city: "", text: "", order_number: "" };

export function ReviewForm({ product, open, onClose, initialOrder = "" }) {
    const [form, setForm] = useState(EMPTY);
    const [rating, setRating] = useState(5);
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && initialOrder) setForm((f) => (f.order_number ? f : { ...f, order_number: initialOrder }));
    }, [open, initialOrder]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const upload = async (e) => {
        const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
        e.target.value = "";
        setUploading(true);
        try {
            for (const file of files) {
                if (file.size > 5 * 1024 * 1024) {
                    toast.error(`${file.name} is larger than 5 MB.`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/review-photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setPhotos((p) => [...p, data.url]);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Photo upload failed.");
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (form.name.trim().length < 2 || form.text.trim().length < 10) {
            toast.error("Please add your name and at least 10 characters of review.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/reviews", {
                product_slug: product.slug,
                rating,
                photo_urls: photos,
                ...form,
                order_number: form.order_number || null,
            });
            toast.success(data.verified ? "Thanks! Verified review submitted for approval." : "Thanks! Your review will appear after approval.");
            setForm(EMPTY);
            setPhotos([]);
            setRating(5);
            onClose();
        } catch (err) {
            toast.error("Could not submit review. Please check the fields.");
        } finally {
            setSaving(false);
        }
    };

    const input = "w-full bg-forest-50/50 border border-forest-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500";

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="review-form-modal" className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-serif text-forest-800 text-2xl">Review {product?.name}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div className="flex gap-1" data-testid="review-rating">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button type="button" key={n} data-testid={`review-star-${n}`} onClick={() => setRating(n)} aria-label={`${n} stars`}>
                                <Star className={`w-8 h-8 transition-transform hover:scale-110 ${n <= rating ? "fill-gold-500 text-gold-500" : "text-forest-200"}`} />
                            </button>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <input data-testid="review-name" value={form.name} onChange={set("name")} placeholder="Your name *" maxLength={60} className={input} />
                        <input data-testid="review-city" value={form.city} onChange={set("city")} placeholder="City" maxLength={40} className={input} />
                    </div>
                    <textarea data-testid="review-text" value={form.text} onChange={set("text")} rows={4} maxLength={1000} placeholder="How did it work for your hair? *" className={input} />
                    <input data-testid="review-order-number" value={form.order_number} onChange={set("order_number")} placeholder="Order number (optional, for Verified Buyer badge)" maxLength={20} className={`${input} uppercase font-mono`} />
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            {photos.map((u) => (
                                <div key={u} className="relative">
                                    <img src={`${API_BASE}${u}`} alt="Uploaded review" className="w-16 h-16 rounded-lg object-cover border border-forest-100" />
                                    <button type="button" onClick={() => setPhotos((p) => p.filter((x) => x !== u))} className="absolute -top-2 -right-2 bg-rose-600 text-white rounded-full p-0.5" aria-label="Remove photo">
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                            {photos.length < 3 && (
                                <label htmlFor="review-photo-input" className="w-16 h-16 rounded-lg border-2 border-dashed border-forest-200 flex items-center justify-center cursor-pointer text-forest-600 hover:border-gold-500">
                                    {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                                </label>
                            )}
                            <input id="review-photo-input" data-testid="review-photo-input" type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={upload} />
                        </div>
                        <p className="text-[11px] text-forest-700/70 mt-1">Add up to 3 photos (before/after welcome). Max 5 MB each.</p>
                    </div>
                    <button type="submit" data-testid="review-submit" disabled={saving || uploading} className="w-full inline-flex items-center justify-center gap-2 bg-forest-800 hover:bg-forest-900 disabled:opacity-70 text-gold-400 font-bold text-sm py-3 rounded-xl border border-gold-500/40">
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />} Submit review
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
